import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, FileDown, Printer, Trash2, Ban, Send, Save, X, Wallet, Plus, Info, ListChecks,
  Paperclip, Pencil, ExternalLink, Building2, ChevronsUpDown, Check, Download, BadgeCheck, ScrollText,
  CheckCircle2, FileText, ChevronDown,
} from "lucide-react";
import api, { API, fmtIDR, fmtDate, printPdf } from "@/lib_app/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import IconAction from "@/components/app/IconAction";
import NumberInput from "@/components/app/NumberInput";
import DateInput from "@/components/app/DateInput";
import { ItemsTab, TermsTab, InfoRow } from "@/components/app/QuotationDetailModal";
import { PoPdfPreview } from "@/components/app/PoPdfPreview";

export const PO_STATUS = {
  draft: { label: "Draft", chip: "bg-[#F4F5F7] text-[#626061] border-[#DCE0E7]" },
  issued: { label: "Terbit", chip: "bg-[#E5EEFA] text-[#3B4A63] border-[#B0C7EA]" },
  partial: { label: "Dibayar Sebagian", chip: "bg-[#FFF3DA] text-[#8C6318] border-[#F2D9A0]" },
  paid: { label: "Lunas", chip: "bg-[#E4F9EC] text-[#3f7a52] border-[#B7E7C7]" },
  void: { label: "Dibatalkan", chip: "bg-[#FDE7E9] text-[#CE6968] border-[#FFB3BF]" },
};

const PO_DOC_CATEGORIES = [
  { key: "quotation_vendor", label: "Quotation Vendor" },
  { key: "invoice_vendor", label: "Invoice Vendor" },
  { key: "bukti_kirim", label: "Bukti Kirim" },
  { key: "lainnya", label: "Lainnya" },
];

const poFileTint = (name) => {
  const ext = (name || "").split(".").pop()?.toLowerCase();
  if (ext === "pdf") return { label: "PDF", bg: "bg-[#FDE7E9]", fg: "text-[#CE6968]" };
  if (["xls", "xlsx", "csv"].includes(ext)) return { label: "XLS", bg: "bg-[#E4F9EC]", fg: "text-[#3EB876]" };
  if (["doc", "docx"].includes(ext)) return { label: "DOC", bg: "bg-[#DDEBFB]", fg: "text-[#2F4B70]" };
  if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return { label: "IMG", bg: "bg-[#E6E2F4]", fg: "text-[#4A3B77]" };
  return { label: "FILE", bg: "bg-[#F4F5F7]", fg: "text-[#626061]" };
};

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const lbl = "text-[10px] font-bold uppercase tracking-[0.12em] text-[#3B4A63] mb-1.5 block";
const emptyGroup = () => ({ title: "", price_mode: "itemized", qty: 1, unit: "ls", price: 0, items: [{ description: "", children: [], qty: 1, unit: "ls", price: 0 }] });

export default function PoDetailModal({ po, vendors, projects, canManage, initialMode, onClose, onSaved, onDeleted }) {
  const isNew = !po.id;
  const navigate = useNavigate();
  const [tab, setTab] = useState("info");
  const [mode, setMode] = useState(isNew ? "edit" : (initialMode === "edit" ? "edit" : "view"));
  const [saving, setSaving] = useState(false);
  const [signers, setSigners] = useState([]);
  const [company, setCompany] = useState(null);
  const [previewFull, setPreviewFull] = useState(false);
  const [vendorPickerOpen, setVendorPickerOpen] = useState(false);
  const [projectPickerOpen, setProjectPickerOpen] = useState(false);
  const [termTemplates, setTermTemplates] = useState([]);
  const [form, setForm] = useState(() => ({
    number: po.number || "",
    vendor_id: po.vendor_id || "",
    vendor_address: po.vendor_address || "",
    vendor_contact: po.vendor_contact || "",
    project_id: po.project_id || "",
    project_title: po.project_title || po.project_name || "",
    date: po.date || new Date().toISOString().slice(0, 10),
    deadline: po.deadline || "",
    quotation_ref: po.quotation_ref || "",
    ship_to: po.ship_to || "",
    ppn_type: po.ppn_type || "ppn",
    vendor_bank_id: po.vendor_bank_id || "",
    signer_id: po.signer_id || "",
    vendor_sign: !!po.vendor_sign,
    vendor_sign_name: po.vendor_sign_name || "",
    vendor_sign_title: po.vendor_sign_title || "",
    terms: po.terms || "",
    terms_groups: (po.terms_groups?.length ? po.terms_groups : []).map((g) => ({ title: g.title || "", items: [...(g.items || [])] })),
    notes: po.notes || "",
    groups: (po.groups?.length ? po.groups : [emptyGroup()]).map((g) => ({
      ...g,
      items: (g.items?.length ? g.items : [{ description: "", children: [], qty: 1, unit: "ls", price: 0 }]).map((it) => ({ ...it, children: [...(it.children || [])] })),
    })),
  }));

  useEffect(() => { api.get("/settings/invoice-signers").then((r) => setSigners(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/settings/company").then((r) => setCompany(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/quotation-term-templates").then((r) => setTermTemplates(r.data || [])).catch(() => {}); }, []);

  const vendor = vendors.find((v) => v.id === form.vendor_id);

  const totals = useMemo(() => {
    const gsubs = form.groups.map((g) => g.price_mode === "lump_sum"
      ? num(g.qty) * num(g.price)
      : g.items.reduce((a, it) => a + num(it.qty) * num(it.price), 0));
    const subtotal = gsubs.reduce((a, b) => a + b, 0);
    const ppn = form.ppn_type === "ppn" ? Math.round(subtotal * 0.11) : 0;
    return { subtotal, ppn, gsubs, billed: subtotal, grand: subtotal + ppn };
  }, [form]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setGroup = (gi, patch) => setForm((f) => ({ ...f, groups: f.groups.map((g, i) => (i === gi ? { ...g, ...patch } : g)) }));
  const setItem = (gi, ii, patch) => setForm((f) => ({
    ...f, groups: f.groups.map((g, i) => (i === gi ? { ...g, items: g.items.map((it, j) => (j === ii ? { ...it, ...patch } : it)) } : g)),
  }));
  const addGroup = () => setForm((f) => ({ ...f, groups: [...f.groups, emptyGroup()] }));
  const removeGroup = (gi) => setForm((f) => ({ ...f, groups: f.groups.filter((_, i) => i !== gi) }));
  const addItem = (gi) => setForm((f) => ({ ...f, groups: f.groups.map((g, i) => (i === gi ? { ...g, items: [...g.items, { description: "", children: [], qty: 1, unit: "ls", price: 0 }] } : g)) }));
  const removeItem = (gi, ii) => setForm((f) => ({ ...f, groups: f.groups.map((g, i) => (i === gi ? { ...g, items: g.items.filter((_, j) => j !== ii) } : g)) }));
  const setPriceMode = (gi, m) => setGroup(gi, { price_mode: m });
  const moveGroup = (gi, dir) => setForm((f) => {
    const arr = [...f.groups]; const to = gi + dir;
    if (to < 0 || to >= arr.length) return f;
    [arr[gi], arr[to]] = [arr[to], arr[gi]];
    return { ...f, groups: arr };
  });
  const moveItem = (gi, ii, dir) => setForm((f) => ({
    ...f, groups: f.groups.map((g, i) => {
      if (i !== gi) return g;
      const arr = [...g.items]; const to = ii + dir;
      if (to < 0 || to >= arr.length) return g;
      [arr[ii], arr[to]] = [arr[to], arr[ii]];
      return { ...g, items: arr };
    }),
  }));

  const setTermGroup = (gi, patch) => setForm((f) => ({ ...f, terms_groups: f.terms_groups.map((g, i) => (i === gi ? { ...g, ...patch } : g)) }));
  const setTermItem = (gi, ii, val) => setForm((f) => ({
    ...f, terms_groups: f.terms_groups.map((g, i) => (i === gi ? { ...g, items: g.items.map((t, j) => (j === ii ? val : t)) } : g)),
  }));
  const addTermGroup = () => setForm((f) => ({ ...f, terms_groups: [...f.terms_groups, { title: "", items: [] }] }));
  const removeTermGroup = (gi) => setForm((f) => ({ ...f, terms_groups: f.terms_groups.filter((_, i) => i !== gi) }));
  const addTermItem = (gi) => setForm((f) => ({ ...f, terms_groups: f.terms_groups.map((g, i) => (i === gi ? { ...g, items: [...g.items, ""] } : g)) }));
  const removeTermItem = (gi, ii) => setForm((f) => ({ ...f, terms_groups: f.terms_groups.map((g, i) => (i === gi ? { ...g, items: g.items.filter((_, j) => j !== ii) } : g)) }));
  const moveTermGroup = (gi, dir) => setForm((f) => {
    const arr = [...f.terms_groups]; const to = gi + dir;
    if (to < 0 || to >= arr.length) return f;
    [arr[gi], arr[to]] = [arr[to], arr[gi]];
    return { ...f, terms_groups: arr };
  });

  const loadTermTemplates = () => api.get("/quotation-term-templates").then((r) => setTermTemplates(r.data || [])).catch(() => {});
  const applyTermTemplate = (tplId) => {
    const tpl = termTemplates.find((t) => t.id === tplId);
    if (!tpl) return;
    setForm((f) => ({ ...f, terms_groups: (tpl.groups || []).map((g) => ({ title: g.title || "", items: [...(g.items || [])] })) }));
    toast.success(`Template "${tpl.name}" diterapkan`);
  };
  const saveTermTemplate = async (name) => {
    const groups = (form.terms_groups || []).filter((g) => (g.title || "").trim() || (g.items || []).some((t) => (t || "").trim()));
    if (!groups.length) { toast.error("Syarat & ketentuan masih kosong"); return; }
    try {
      await api.post("/quotation-term-templates", { name: name.trim(), groups });
      await loadTermTemplates();
      toast.success("Template disimpan");
    } catch { toast.error("Gagal menyimpan template"); }
  };
  const deleteTermTemplate = async (tplId) => {
    try { await api.delete(`/quotation-term-templates/${tplId}`); await loadTermTemplates(); toast.success("Template dihapus"); }
    catch { toast.error("Gagal menghapus template"); }
  };

  const chooseVendor = (v) => {
    const pic = (v.pics || []).find((p) => p.is_default) || (v.pics || [])[0];
    setForm((f) => ({
      ...f,
      vendor_id: v.id,
      vendor_address: v.address || "",
      vendor_contact: (v.pics || []).find((p) => p.is_default)?.phone || (v.pics || [])[0]?.phone || "",
      ship_to: f.ship_to || company?.address || "",
      vendor_sign_name: pic?.name || f.vendor_sign_name,
      vendor_sign_title: pic?.position || f.vendor_sign_title,
    }));
    setVendorPickerOpen(false);
  };

  const pickProject = (pid) => {
    const p = projects.find((x) => x.id === pid);
    setForm((f) => ({ ...f, project_id: pid, project_title: p?.name || (pid ? f.project_title : f.project_title) }));
    setProjectPickerOpen(false);
  };

  const save = async () => {
    if (!form.vendor_id) return toast.error("Pilih vendor terlebih dahulu");
    setSaving(true);
    try {
      const payload = {
        ...form,
        groups: form.groups.map((g) => ({
          title: g.title, price_mode: g.price_mode, qty: num(g.qty), unit: g.unit, price: num(g.price),
          items: g.items.map((it) => ({
            description: it.description, qty: num(it.qty), unit: it.unit, price: num(it.price),
            children: (it.children || []).map((c) => String(c || "").trim()).filter(Boolean),
          })),
        })),
        project_id: form.project_id || null,
        deadline: form.deadline || null,
        vendor_bank_id: form.vendor_bank_id || null,
        signer_id: form.signer_id || null,
      };
      const res = isNew ? await api.post("/purchase-orders", payload) : await api.patch(`/purchase-orders/${po.id}`, payload);
      toast.success(isNew ? "PO dibuat" : "PO diperbarui");
      setMode("view");
      onSaved(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan PO"); }
    finally { setSaving(false); }
  };

  const act = async (path, msg) => {
    try {
      const res = await api.post(`/purchase-orders/${po.id}/${path}`);
      toast.success(msg);
      onSaved(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/purchase-orders/${po.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `PO-${(po.number || "").replace(/\//g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Gagal mengunduh PDF"); }
  };

  const remove = async () => {
    if (!window.confirm(`Hapus PO ${po.number}?`)) return;
    try { await api.delete(`/purchase-orders/${po.id}`); toast.success("PO dihapus"); onDeleted(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); }
  };

  const isEdit = mode === "edit";
  const st = PO_STATUS[po.status] || PO_STATUS.draft;
  const tabs = [
    { key: "info", label: "Informasi", icon: Info },
    { key: "items", label: "Item & Harga", icon: ListChecks },
    { key: "terms", label: "Syarat & Ketentuan", icon: ScrollText },
    ...(isNew ? [] : [
      { key: "pay", label: "Pembayaran", icon: Wallet },
      { key: "docs", label: "Lampiran", icon: Paperclip },
    ]),
  ];

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-[1560px] w-[96vw] max-h-[92vh] overflow-hidden rounded-3xl border-0 p-0"
        data-testid="po-modal"
        style={{
          background: "linear-gradient(160deg, rgba(255,255,255,0.96) 0%, rgba(232,240,254,0.92) 100%)",
          boxShadow: "0 32px 80px -20px rgba(59,74,99,0.30)",
        }}
      >
        <DialogTitle className="sr-only">Purchase Order {po.number || "Baru"}</DialogTitle>
        <DialogDescription className="sr-only">Detail purchase order</DialogDescription>

        <div className="flex h-[92vh] max-h-[92vh]">
          <div className={`${previewFull ? "hidden" : "block"} w-full lg:w-1/2 lg:shrink-0 min-w-0 overflow-y-auto p-6 space-y-5`}>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#6B8FE2] font-bold" data-testid="po-modal-label">
              {isNew ? "Buat Purchase Order" : "Detail Purchase Order"}
            </p>

            {/* HERO */}
            <div
              className="rounded-2xl p-5 backdrop-blur-xl"
              data-testid="po-hero"
              style={{
                background: "linear-gradient(135deg, rgba(190,210,240,0.55), rgba(232,240,254,0.35))",
                border: "1px solid rgba(150,175,220,0.45)",
                boxShadow: "0 10px 30px -12px rgba(20,22,31,0.12), inset 0 1px 0 rgba(255,255,255,0.55)",
              }}
            >
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <span className="inline-flex items-center h-6 px-2.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-white/70 text-[#3B4A63] border border-white/80 font-mono" data-testid="po-modal-number">
                      {form.number || "Nomor otomatis saat disimpan"}
                    </span>
                    <span className={`inline-flex items-center h-6 px-2.5 rounded-full text-[10px] font-bold uppercase tracking-widest border ${st.chip}`} data-testid="po-modal-status">{st.label}</span>
                    <span className={`inline-flex items-center h-6 px-2.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${form.ppn_type === "ppn" ? "bg-[#D8EEDF] text-[#3f7a52]" : "bg-[#DCE0E7] text-[#626061]"}`}>
                      {form.ppn_type === "ppn" ? "PPN 11%" : "Non-PPN"}
                    </span>
                  </div>
                  <h2 className="text-2xl font-bold text-[#090808] leading-tight truncate" data-testid="po-modal-vendor">{vendor?.name || po.vendor_name || "Pilih Vendor"}</h2>
                  {form.project_title && (
                    <p className="text-xs font-semibold text-[#3B4A63] mt-0.5 truncate" data-testid="po-modal-project">{form.project_title}</p>
                  )}
                </div>
                {form.project_id && (
                  <button
                    onClick={() => { onClose(); navigate(`/projects?open=${form.project_id}`); }}
                    data-testid="po-open-project"
                    className="inline-flex items-center h-7 px-3 rounded-full text-[11px] font-bold uppercase tracking-widest bg-white/70 text-[#3B4A63] border border-white/80 shrink-0 shadow-sm gap-1.5 hover:bg-white"
                  >
                    <ExternalLink className="w-3 h-3" /> Proyek
                  </button>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Tanggal PO</div>
                  <div className="text-sm font-semibold text-[#090808] font-num tabular-nums">{form.date ? fmtDate(form.date) : "-"}</div>
                </div>
                <div className="w-px h-8 bg-white/70 hidden sm:block" />
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Deadline</div>
                  <div className="text-sm font-semibold text-[#090808] font-num tabular-nums" data-testid="po-hero-deadline">{form.deadline ? fmtDate(form.deadline) : "—"}</div>
                </div>
                <div className="w-px h-8 bg-white/70 hidden sm:block" />
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Ref. Quotation</div>
                  <div className="text-sm font-semibold text-[#090808] font-num tabular-nums truncate max-w-[180px]" data-testid="po-hero-quoref">{form.quotation_ref || "—"}</div>
                </div>
                {!isNew && canManage && po.status === "draft" && (
                  <>
                    <div className="flex-1" />
                    <button
                      onClick={() => act("issue", "PO diterbitkan")} data-testid="po-issue-btn"
                      className="h-9 rounded-full gap-1.5 px-4 font-semibold shadow-sm text-xs bg-gradient-to-r from-[#57D28A] to-[#3EB876] hover:from-[#3EB876] hover:to-[#2FA365] text-white inline-flex items-center"
                    >
                      <BadgeCheck className="w-3.5 h-3.5" /> Terbitkan
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* STAT STRIP */}
            <div className="grid grid-cols-3 rounded-2xl bg-white/70 border border-white/80 backdrop-blur-xl overflow-hidden" style={{ boxShadow: "0 6px 18px rgba(59,74,99,0.08)" }} data-testid="po-totals-strip">
              <div className="p-4 border-r border-[#DCE0E7]/60">
                <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Nilai Item</div>
                <div className="text-lg font-bold text-[#090808] font-num tabular-nums mt-1">{fmtIDR(totals.subtotal)}</div>
              </div>
              <div className="p-4 border-r border-[#DCE0E7]/60">
                <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">{form.ppn_type === "ppn" ? "PPN 11%" : "PPN"}</div>
                <div className="text-lg font-bold font-num tabular-nums mt-1 text-[#626061]" data-testid="po-ppn-amount">{form.ppn_type === "ppn" ? fmtIDR(totals.ppn) : "Non-PPN"}</div>
              </div>
              <div className="p-4">
                <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Total PO</div>
                <div className="text-lg font-bold text-[#3B4A63] font-num tabular-nums mt-1" data-testid="po-grand-total">{fmtIDR(totals.grand)}</div>
              </div>
            </div>

            {/* TAB BAR */}
            <div className="flex items-center justify-between border-b border-[#DCE0E7] gap-3">
              <div className="flex items-center flex-wrap flex-1">
                {tabs.map((t) => {
                  const Ico = t.icon;
                  const active = tab === t.key;
                  return (
                    <button
                      key={t.key} onClick={() => setTab(t.key)} data-testid={`po-tab-${t.key}`}
                      className={`inline-flex items-center gap-1.5 px-3 py-3 text-[13px] transition-colors whitespace-nowrap border-b-2 ${
                        active ? (isEdit ? "text-[#3B4A63] border-[#3B4A63] font-bold" : "text-[#090808] border-[#090808] font-bold") : "text-[#A0A3A8] border-transparent hover:text-[#626061]"
                      }`}
                    >
                      <Ico className="w-3.5 h-3.5" /> {t.label}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-2 pb-2">
                {!isEdit && canManage && po.status !== "void" && !isNew && (
                  <IconAction icon={Pencil} label="Edit" variant="info" size="sm" onClick={() => setMode("edit")} testid="po-edit-toggle" />
                )}
                {isEdit && !isNew && (
                  <>
                    <IconAction icon={X} label="Batal" size="sm" onClick={() => setMode("view")} disabled={saving} testid="po-edit-cancel" />
                    <IconAction icon={Save} label={saving ? "Menyimpan…" : "Simpan"} variant="primary" size="sm" onClick={save} disabled={saving} testid="po-save-btn" />
                  </>
                )}
              </div>
            </div>

            {/* TAB CONTENT */}
            {tab === "info" && (!isEdit ? (
              <div className="space-y-0 rounded-2xl bg-white/50 border border-[#E5EEFA]/60 px-5 pt-2 pb-4" data-testid="po-info-read">
                <InfoRow label="No. PO" value={form.number || "-"} valueClass="font-num" />
                <InfoRow label="Vendor" value={vendor?.name || po.vendor_name || "-"} />
                <InfoRow label="Alamat Vendor" value={form.vendor_address || "-"} />
                <InfoRow label="Kontak Vendor" value={form.vendor_contact || "-"} />
                <InfoRow label="Ref. Quotation Vendor" value={form.quotation_ref || "-"} valueClass="font-num" />
                <InfoRow label="Proyek" value={form.project_title || "-"} />
                <InfoRow label="Jenis PO" value={form.ppn_type === "ppn" ? "PPN 11%" : "Non-PPN"} />
                <InfoRow label="Tanggal PO" value={form.date ? fmtDate(form.date) : "-"} valueClass="font-num" />
                <InfoRow label="Deadline Pengiriman" value={form.deadline ? fmtDate(form.deadline) : "-"} valueClass="font-num" />
                <InfoRow label="Ship To" value={form.ship_to || company?.address || "-"} />
                <InfoRow label="Penandatangan" value={signers.find((sg) => sg.id === form.signer_id)?.name || signers.find((sg) => sg.is_default)?.name || "-"} />
                <InfoRow label="TTD Vendor" value={form.vendor_sign ? `Ya · ${form.vendor_sign_name || "-"}${form.vendor_sign_title ? ` (${form.vendor_sign_title})` : ""}` : "Tidak ditampilkan"} />
                <InfoRow label="Catatan Internal" value={form.notes || "-"} />
              </div>
            ) : (
              <div className="rounded-2xl border-2 border-[#B0C7EA] bg-[#F5F9FF]/50 p-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label className={lbl}>Vendor *</Label>
                    <Popover open={vendorPickerOpen} onOpenChange={setVendorPickerOpen}>
                      <PopoverTrigger asChild>
                        <Button type="button" variant="outline" data-testid="po-vendor-picker" className="w-full justify-between bg-white h-11 font-normal">
                          {vendor ? (
                            <span className="flex items-center gap-2 min-w-0">
                              {vendor.logo ? (
                                <img src={vendor.logo} alt="" className="w-6 h-6 rounded-md object-cover" />
                              ) : (
                                <div className="w-6 h-6 rounded-md bg-[#DCE0E7] text-[#090808] flex items-center justify-center"><Building2 className="w-3 h-3" /></div>
                              )}
                              <span className="truncate font-semibold text-[#090808]">{vendor.name}</span>
                            </span>
                          ) : (
                            <span className="text-[#A0A3A8]">Pilih vendor dari database...</span>
                          )}
                          <ChevronsUpDown className="w-4 h-4 text-[#A0A3A8] shrink-0" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start" onWheel={(e) => e.stopPropagation()}>
                        <Command>
                          <CommandInput placeholder="Cari vendor..." data-testid="po-vendor-search" />
                          <CommandList className="max-h-72">
                            <CommandEmpty>
                              <div className="text-center py-4 text-sm text-[#A0A3A8]">Vendor tidak ditemukan</div>
                            </CommandEmpty>
                            <CommandGroup>
                              {vendors.map((v) => (
                                <CommandItem key={v.id} value={v.name} onSelect={() => chooseVendor(v)} data-testid={`po-vendor-option-${v.id}`} className="gap-2">
                                  {v.logo ? (
                                    <img src={v.logo} alt="" className="w-7 h-7 rounded-md object-cover" />
                                  ) : (
                                    <div className="w-7 h-7 rounded-md bg-[#DCE0E7] text-[#090808] flex items-center justify-center"><Building2 className="w-3.5 h-3.5" /></div>
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold truncate">{v.name}</div>
                                    <div className="text-[10px] text-[#A0A3A8] truncate">{v.address || "Alamat belum diisi"}</div>
                                  </div>
                                  {form.vendor_id === v.id && <Check className="w-4 h-4 text-[#090808]" />}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="md:col-span-2">
                    <Label className={lbl}>Alamat Vendor (tampil di PO)</Label>
                    <Textarea value={form.vendor_address || ""} onChange={(e) => setField("vendor_address", e.target.value)} rows={2} className="rounded-xl bg-white w-full" data-testid="po-vendor-address" placeholder="Alamat vendor" />
                  </div>

                  <div>
                    <Label className={lbl}>Kontak Vendor (tampil di PO)</Label>
                    <Input value={form.vendor_contact || ""} onChange={(e) => setField("vendor_contact", e.target.value)} className="h-10 rounded-xl bg-white" data-testid="po-vendor-contact" placeholder="Nama PIC / telepon" />
                  </div>
                  <div>
                    <Label className={lbl}>Ref. Quotation Vendor</Label>
                    <Input value={form.quotation_ref || ""} onChange={(e) => setField("quotation_ref", e.target.value)} className="h-10 rounded-xl bg-white font-num" data-testid="po-quotation-ref" placeholder="Nomor quotation dari vendor" />
                  </div>

                  <div>
                    <Label className={lbl}>Proyek Terkait</Label>
                    <Popover open={projectPickerOpen} onOpenChange={setProjectPickerOpen}>
                      <PopoverTrigger asChild>
                        <Button type="button" variant="outline" data-testid="po-project-select" className="w-full justify-between bg-white h-10 font-normal">
                          <span className={`truncate ${form.project_id ? "font-semibold text-[#090808]" : "text-[#A0A3A8]"}`}>
                            {(() => {
                              const sel = projects.find((p) => p.id === form.project_id);
                              return sel ? (sel.client_name || sel.customer || sel.name) : "Tidak terkait proyek";
                            })()}
                          </span>
                          <ChevronsUpDown className="w-4 h-4 text-[#A0A3A8] shrink-0" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start" onWheel={(e) => e.stopPropagation()}>
                        <Command>
                          <CommandInput placeholder="Cari proyek..." data-testid="po-project-search" />
                          <CommandList className="max-h-72">
                            <CommandEmpty>
                              <div className="text-center py-4 text-sm text-[#A0A3A8]">Proyek tidak ditemukan</div>
                            </CommandEmpty>
                            <CommandGroup>
                              <CommandItem value="Tidak terkait proyek" onSelect={() => pickProject("")} data-testid="po-project-option-none" className="gap-2">
                                <span className="text-sm text-[#626061]">Tidak terkait proyek</span>
                                {!form.project_id && <Check className="w-4 h-4 text-[#090808] ml-auto" />}
                              </CommandItem>
                              {projects.map((p) => (
                                <CommandItem key={p.id} value={`${p.client_name || p.customer || ""} ${p.name}`} onSelect={() => pickProject(p.id)} data-testid={`po-project-option-${p.id}`} className="gap-2">
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold truncate">{p.client_name || p.customer || "—"}</div>
                                    <div className="text-[10px] text-[#A0A3A8] truncate">{p.name}</div>
                                  </div>
                                  {form.project_id === p.id && <Check className="w-4 h-4 text-[#090808]" />}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label className={lbl}>Judul Proyek (tampil di PO)</Label>
                    <Input value={form.project_title || ""} onChange={(e) => setField("project_title", e.target.value)} className="h-10 rounded-xl bg-white" data-testid="po-project-title" placeholder="mis. Pengadaan LED Screen Gedung A" />
                  </div>

                  <div>
                    <Label className={lbl}>No. PO (kosongkan = otomatis)</Label>
                    <Input value={form.number} onChange={(e) => setField("number", e.target.value)} className="h-10 rounded-xl bg-white font-num" data-testid="po-number" placeholder="001/PO-IDS/IX/2026" />
                  </div>
                  <div>
                    <Label className={lbl}>Jenis PO</Label>
                    <div className="flex items-center gap-2 h-10">
                      {[["ppn", "PPN 11%"], ["non_ppn", "Non-PPN"]].map(([v, l]) => (
                        <button key={v} onClick={() => setField("ppn_type", v)} data-testid={`po-ppn-${v}`}
                          className={`h-8 px-3 rounded-full text-xs font-semibold border ${form.ppn_type === v ? "bg-[#3B4A63] text-white border-[#3B4A63]" : "bg-white text-[#626061] border-[#DCE0E7]"}`}>{l}</button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className={lbl}>Tanggal PO</Label>
                    <Input type="date" value={form.date} onChange={(e) => setField("date", e.target.value)} className="h-10 rounded-xl bg-white" data-testid="po-date" />
                  </div>
                  <div>
                    <Label className={lbl}>Deadline Pengiriman</Label>
                    <Input type="date" value={form.deadline || ""} onChange={(e) => setField("deadline", e.target.value)} className="h-10 rounded-xl bg-white" data-testid="po-deadline" />
                  </div>

                  <div className="md:col-span-2">
                    <Label className={lbl}>Ship To (alamat pengiriman)</Label>
                    <Textarea value={form.ship_to || ""} onChange={(e) => setField("ship_to", e.target.value)} rows={2} className="rounded-xl bg-white" data-testid="po-ship-to" placeholder={company?.address || "Alamat tujuan pengiriman"} />
                  </div>

                  <div>
                    <Label className={lbl}>Penandatangan</Label>
                    <Select value={form.signer_id || ""} onValueChange={(v) => setField("signer_id", v)}>
                      <SelectTrigger className="h-10 rounded-xl bg-white" data-testid="po-signer-select"><SelectValue placeholder="Pilih penandatangan" /></SelectTrigger>
                      <SelectContent>
                        {signers.map((sg) => <SelectItem key={sg.id} value={sg.id} data-testid={`po-signer-option-${sg.id}`}>{sg.name} · {sg.title}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className={lbl}>Tanda Tangan Vendor</Label>
                    <button
                      type="button" onClick={() => setField("vendor_sign", !form.vendor_sign)} data-testid="po-vendor-sign-toggle"
                      className={`w-full h-10 px-3 rounded-xl border flex items-center justify-between gap-2 transition-colors ${
                        form.vendor_sign ? "bg-[#E5EEFA] border-[#B0C7EA] text-[#3B4A63]" : "bg-white border-[#DCE0E7] text-[#626061] hover:border-[#B0C7EA]"
                      }`}
                    >
                      <span className="text-[13px] font-semibold truncate">Acknowledged by vendor</span>
                      <span className={`w-9 h-5 rounded-full shrink-0 relative transition-colors ${form.vendor_sign ? "bg-[#3B4A63]" : "bg-[#DCE0E7]"}`}>
                        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${form.vendor_sign ? "left-[18px]" : "left-0.5"}`} />
                      </span>
                    </button>
                  </div>

                  {form.vendor_sign && (
                    <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4 rounded-2xl border border-[#B0C7EA] bg-white p-4">
                      <div>
                        <Label className={lbl}>Nama Penandatangan Vendor</Label>
                        <Input value={form.vendor_sign_name || ""} onChange={(e) => setField("vendor_sign_name", e.target.value)} className="h-10 rounded-xl bg-white" data-testid="po-vendor-sign-name" placeholder="Nama PIC vendor" />
                      </div>
                      <div>
                        <Label className={lbl}>Jabatan</Label>
                        <Input value={form.vendor_sign_title || ""} onChange={(e) => setField("vendor_sign_title", e.target.value)} className="h-10 rounded-xl bg-white" data-testid="po-vendor-sign-title" placeholder="mis. Direktur" />
                      </div>
                    </div>
                  )}

                  <div className="md:col-span-2">
                    <Label className={lbl}>Catatan Internal</Label>
                    <Textarea value={form.notes || ""} onChange={(e) => setField("notes", e.target.value)} rows={2} className="rounded-xl bg-white" data-testid="po-notes" />
                  </div>
                </div>
              </div>
            ))}

            {tab === "items" && (
              <ItemsTab
                form={form} mode={isEdit ? "edit" : "read"} totals={totals}
                setGroup={setGroup} setItem={setItem} addGroup={addGroup} removeGroup={removeGroup}
                addItem={addItem} removeItem={removeItem} setPriceMode={setPriceMode}
                moveGroup={moveGroup} moveItem={moveItem}
              />
            )}

            {tab === "terms" && (
              <div className="space-y-4">
                <TermsTab
                  groups={form.terms_groups || []} mode={isEdit ? "edit" : "read"}
                  setTermGroup={setTermGroup} setTermItem={setTermItem}
                  addTermGroup={addTermGroup} removeTermGroup={removeTermGroup}
                  addTermItem={addTermItem} removeTermItem={removeTermItem}
                  moveTermGroup={moveTermGroup}
                  templates={termTemplates}
                  onApplyTemplate={applyTermTemplate}
                  onSaveTemplate={saveTermTemplate}
                  onDeleteTemplate={deleteTermTemplate}
                />
              </div>
            )}

            {tab === "pay" && <PoPayments po={po} canManage={canManage} onChanged={(fresh) => onSaved(fresh)} />}
            {tab === "docs" && <PoDocs po={po} canManage={canManage} />}

            {/* FOOTER */}
            <div className="pt-4 border-t border-[#E5EEFA]/60 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                {!isNew && canManage && po.status === "draft" && (
                  <IconAction icon={Trash2} label="Hapus PO" variant="danger" onClick={remove} testid="po-delete-btn" />
                )}
                {!isNew && canManage && ["issued", "partial", "paid"].includes(po.status) && (
                  <IconAction icon={Ban} label="Batalkan PO" variant="danger" onClick={() => act("void", "PO dibatalkan")} testid="po-void-btn" />
                )}
              </div>
              <div className="flex items-center gap-2">
                {isNew && canManage && (
                  <IconAction icon={Save} label={saving ? "Menyimpan…" : "Simpan PO"} variant="primary" onClick={save} disabled={saving} testid="po-save-btn-new" />
                )}
                {!isNew && (
                  <>
                    <IconAction icon={FileDown} label="Unduh PDF" variant="success" onClick={downloadPdf} testid="po-download-btn" />
                    <IconAction icon={Printer} label="Cetak PDF" variant="info" onClick={() => printPdf(`/purchase-orders/${po.id}/pdf`).catch(() => toast.error("Gagal mencetak"))} testid="po-print-btn" />
                  </>
                )}
                <IconAction icon={X} label="Tutup" onClick={onClose} testid="po-modal-close" />
              </div>
            </div>

          </div>

          {/* PREVIEW */}
          <div className={`${previewFull ? "w-full" : "hidden lg:block lg:w-1/2"} min-w-0 border-l border-[#DCE0E7] bg-[#EDF1F7] relative`}>
            <PoPdfPreview
              form={{ ...form, status: po.status }}
              totals={totals}
              company={company}
              qrDataUri={po.qr_data_uri || null}
              ownerName={signers.find((sg) => sg.id === form.signer_id)?.name || signers.find((sg) => sg.is_default)?.name}
              ownerJobTitle={signers.find((sg) => sg.id === form.signer_id)?.title || signers.find((sg) => sg.is_default)?.title}
              party={{ name: vendor?.name || po.vendor_name, address: vendor?.address || po.vendor_address, contact: po.vendor_contact }}
              full={previewFull}
              onToggleFull={() => setPreviewFull((v) => !v)}
            />
          </div>
        </div>

      </DialogContent>
    </Dialog>
  );
}

function PoDocs({ po, canManage }) {
  const [rows, setRows] = useState(po.attachments || []);
  const [category, setCategory] = useState("quotation_vendor");
  const [filter, setFilter] = useState("all");
  const [panelOpen, setPanelOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get(`/purchase-orders/${po.id}/attachments`).then((r) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [po.id]);

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("files", f));
      fd.append("category", category);
      await api.post(`/purchase-orders/${po.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Lampiran diupload");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal upload lampiran"); }
    finally { setBusy(false); }
  };

  const del = async (att) => {
    if (!window.confirm(`Hapus lampiran "${att.filename}"?`)) return;
    try { await api.delete(`/purchase-orders/${po.id}/attachments/${att.id}`); toast.success("Lampiran dihapus"); load(); }
    catch { toast.error("Gagal menghapus lampiran"); }
  };

  const open = (att) => {
    const token = localStorage.getItem("sp_token");
    window.open(`${API}/purchase-orders/${po.id}/attachments/${att.id}/download?auth=${encodeURIComponent(token || "")}`, "_blank");
  };

  const counts = {
    all: rows.length,
    ...Object.fromEntries(PO_DOC_CATEGORIES.map((c) => [c.key, rows.filter((a) => a.category === c.key).length])),
  };
  const visibleGroups = PO_DOC_CATEGORIES
    .filter((c) => filter === "all" || filter === c.key)
    .map((c) => ({ cat: c, items: rows.filter((a) => a.category === c.key) }))
    .filter((g) => g.items.length > 0);

  return (
    <div className="space-y-3" data-testid="po-docs">
      {/* HEADER: filter chips + upload toggle */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap flex-1 min-w-0">
          {[["all", "Semua"], ...PO_DOC_CATEGORIES.map((c) => [c.key, c.label])].map(([key, label]) => (
            <button
              key={key} type="button" onClick={() => setFilter(key)}
              data-testid={`po-doc-filter-${key}`}
              className={`h-8 px-3 rounded-full text-[11px] font-semibold border transition-all inline-flex items-center gap-1.5 ${
                filter === key ? "bg-[#090808] text-white border-[#090808]" : "bg-white text-[#626061] border-[#DCE0E7] hover:border-[#3B4A63]"
              }`}
            >
              {label}
              <span className={`font-num tabular-nums ${filter === key ? "text-white/70" : "text-[#A0A3A8]"}`}>{counts[key]}</span>
            </button>
          ))}
        </div>
        {canManage && (
          <button
            type="button" onClick={() => setPanelOpen((v) => !v)} data-testid="po-doc-upload-toggle"
            className="h-8 px-3.5 rounded-full bg-gradient-to-r from-[#6B8FE2] to-[#3B4A63] text-white text-[11px] font-semibold inline-flex items-center gap-1.5 shrink-0"
          >
            <Plus className="w-3.5 h-3.5" /> Upload
            <ChevronDown className={`w-3 h-3 transition-transform ${panelOpen ? "rotate-180" : ""}`} />
          </button>
        )}
      </div>

      {/* UPLOAD PANEL */}
      {canManage && panelOpen && (
        <div className="rounded-2xl border border-[#B0C7EA]/70 bg-[#F5F9FF]/70 p-4 space-y-3" data-testid="po-doc-upload-panel">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63] mb-1.5">Jenis Dokumen</div>
            <div className="flex flex-wrap items-center gap-1.5">
              {PO_DOC_CATEGORIES.map((c) => (
                <button key={c.key} type="button" onClick={() => setCategory(c.key)} data-testid={`po-doc-cat-${c.key}`}
                  className={`h-8 px-3 rounded-full text-[11px] font-semibold border transition-all ${category === c.key ? "bg-[#3B4A63] text-white border-[#3B4A63]" : "bg-white text-[#626061] border-[#DCE0E7] hover:border-[#3B4A63]"}`}>
                  {c.label}
                </button>
              ))}
            </div>
          </div>
          <input ref={fileRef} type="file" multiple className="hidden" data-testid="po-doc-file-input"
            accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt"
            onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="po-doc-upload-btn"
            className="w-full h-11 rounded-full bg-[#090808] text-white text-sm font-semibold hover:bg-black inline-flex items-center justify-center gap-2 disabled:opacity-60">
            <Paperclip className="w-4 h-4" /> {busy ? "Mengupload…" : `Pilih file — ${PO_DOC_CATEGORIES.find((c) => c.key === category)?.label}`}
          </button>
          <div className="text-[10px] text-[#A0A3A8] text-center">PDF · DOC · XLS · Gambar · maks 15MB per file — juga muncul di Lampiran proyek</div>
        </div>
      )}

      {/* LIST */}
      {visibleGroups.length === 0 ? (
        <div className="rounded-2xl bg-white/50 border border-[#DCE0E7]/60 p-8 text-center text-sm text-[#A0A3A8]" data-testid="po-docs-empty">
          {filter === "all" ? "Belum ada lampiran. Unggah quotation dari vendor di sini." : "Belum ada lampiran pada kategori ini."}
        </div>
      ) : (
        <div className="rounded-2xl border border-[#DCE0E7]/70 bg-white overflow-hidden">
          {visibleGroups.map((g) => (
            <div key={g.cat.key} data-testid={`po-doc-group-${g.cat.key}`}>
              <div className="px-4 py-2 bg-[#F7F8FA] border-y border-[#DCE0E7]/60 first:border-t-0 flex items-center gap-2">
                <span className="text-[9px] uppercase tracking-widest font-bold text-[#626061]">{g.cat.label}</span>
                <span className="text-[9px] font-num tabular-nums text-[#A0A3A8]">{g.items.length}</span>
              </div>
              <div className="divide-y divide-[#DCE0E7]/60">
                {g.items.map((a) => {
                  const ic = poFileTint(a.filename);
                  return (
                    <div key={a.id} data-testid={`po-doc-row-${a.id}`} className="px-4 py-3 flex items-center gap-3 hover:bg-[#FAFBFC] transition">
                      <div className={`w-10 h-10 rounded-xl ${ic.bg} ${ic.fg} flex items-center justify-center font-bold text-[10px] shrink-0`}>{ic.label}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-semibold text-[#090808] truncate">{a.filename}</div>
                        <div className="text-[11px] text-[#A0A3A8] mt-0.5 flex items-center gap-1.5 flex-wrap">
                          {a.uploaded_by_name && <span className="truncate">{a.uploaded_by_name}</span>}
                          {a.uploaded_at && <span className="font-num tabular-nums">{fmtDate(a.uploaded_at)}</span>}
                          {a.size ? <span className="font-num tabular-nums">{Math.max(1, Math.round(a.size / 1024))} KB</span> : null}
                        </div>
                      </div>
                      <button onClick={() => open(a)} title="Buka / Unduh" data-testid={`po-doc-open-${a.id}`} className="w-8 h-8 rounded-full hover:bg-[#F4F5F7] text-[#3B4A63] flex items-center justify-center shrink-0"><Download className="w-3.5 h-3.5" /></button>
                      {canManage && (
                        <button onClick={() => del(a)} title="Hapus" data-testid={`po-doc-delete-${a.id}`} className="w-8 h-8 rounded-full hover:bg-[#FDE7E9] text-[#CE6968] flex items-center justify-center shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PoPayments({ po, canManage, onChanged }) {
  const [data, setData] = useState({ payments: po.payments || [], paid_amount: po.paid_amount || 0, outstanding: po.outstanding || 0 });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pay, setPay] = useState({ amount: 0, date: new Date().toISOString().slice(0, 10), method: "", note: "" });
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);

  const grandTotal = po.grand_total || 0;
  const paid = data.paid_amount || 0;
  const outstanding = Math.max(0, grandTotal - paid);
  const pct = grandTotal > 0 ? Math.min(100, (paid * 100) / grandTotal) : 0;
  const locked = po.status === "draft" || po.status === "void";

  const reload = async () => {
    const [p, fresh] = await Promise.all([api.get(`/purchase-orders/${po.id}/payments`), api.get(`/purchase-orders/${po.id}`)]);
    setData(p.data);
    onChanged(fresh.data);
  };

  const pickFile = (f) => {
    if (!f) { setFile(null); return; }
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    if (!["pdf", "png", "jpg", "jpeg", "webp", "doc", "docx", "xls", "xlsx"].includes(ext)) return toast.error("Ekstensi tidak diizinkan");
    if (f.size > 15 * 1024 * 1024) return toast.error("File > 15MB");
    setFile(f);
  };

  const add = async () => {
    if (!Number(pay.amount)) return toast.error("Nominal wajib diisi");
    if (!pay.note.trim()) return toast.error("Keterangan wajib diisi");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("amount", String(Number(pay.amount)));
      fd.append("note", pay.note);
      fd.append("date", pay.date);
      if (pay.method) fd.append("method", pay.method);
      if (file) fd.append("attachment", file);
      await api.post(`/purchase-orders/${po.id}/payments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Pembayaran dicatat");
      setPay({ amount: 0, date: new Date().toISOString().slice(0, 10), method: "", note: "" });
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      setOpen(false);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mencatat pembayaran"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus catatan pembayaran ini?")) return;
    try { await api.delete(`/purchase-orders/${po.id}/payments/${id}`); toast.success("Pembayaran dihapus"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const openAttachment = async (p) => {
    try {
      const res = await api.get(`/purchase-orders/${po.id}/payments/${p.id}/attachment`, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(res.data);
      window.open(blobUrl, "_blank");
      setTimeout(() => URL.revokeObjectURL(blobUrl), 120000);
    } catch {
      toast.error("Gagal membuka lampiran");
    }
  };

  return (
    <div className="space-y-4" data-testid="po-payments">
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-2xl bg-[#E4F9EC] border border-[#B7E7C7] p-4">
          <div className="text-[9px] uppercase tracking-widest text-[#4A6D53] font-bold">Sudah Dibayar</div>
          <div className="text-lg font-bold text-[#3EB876] mt-0.5 font-num tabular-nums" data-testid="po-paid-amount">{fmtIDR(paid)}</div>
          <div className="text-[10px] text-[#4A6D53] mt-0.5 font-num tabular-nums">{pct.toFixed(2)}% dari nilai PO</div>
        </div>
        <div className="rounded-2xl bg-[#FDE7E9] border border-[#FFB3BF] p-4">
          <div className="text-[9px] uppercase tracking-widest text-[#8E4747] font-bold">Sisa</div>
          <div className="text-lg font-bold text-[#CE6968] mt-0.5 font-num tabular-nums" data-testid="po-outstanding">{fmtIDR(outstanding)}</div>
          <div className="text-[10px] text-[#8E4747] mt-0.5 font-num tabular-nums">{Math.max(0, 100 - pct).toFixed(2)}% sisa</div>
        </div>
        <div className="rounded-2xl bg-[#E5EEFA] border border-[#B0C7EA] p-4">
          <div className="text-[9px] uppercase tracking-widest text-[#3B4A63] font-bold">Total Nilai PO</div>
          <div className="text-lg font-bold text-[#3B4A63] mt-0.5 font-num tabular-nums">{fmtIDR(grandTotal)}</div>
          <div className="text-[10px] text-[#3B4A63] mt-0.5">{(data.payments || []).length} pembayaran tercatat</div>
        </div>
      </div>

      <div className="h-1.5 rounded-full bg-white overflow-hidden border border-[#DCE0E7]/60">
        <div className="h-full bg-gradient-to-r from-[#57D28A] to-[#3EB876] transition-all" style={{ width: `${pct}%` }} />
      </div>

      {canManage && !locked && (
        <div className="rounded-2xl overflow-hidden bg-white/60 border border-[#B0C7EA]/60">
          <button type="button" onClick={() => setOpen((v) => !v)} data-testid="po-pay-form-toggle" className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/80 transition-colors">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#6B8FE2] to-[#3B4A63] text-white flex items-center justify-center"><Plus className="w-4 h-4" /></div>
              <div className="text-sm font-bold text-[#090808]">Catat Pembayaran</div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-[#6B8FE2] font-semibold hidden sm:inline">Sisa: <span className="font-num tabular-nums">{fmtIDR(outstanding)}</span></span>
              <ChevronDown className={`w-4 h-4 text-[#3B4A63] transition-transform ${open ? "rotate-180" : ""}`} />
            </div>
          </button>
          {open && (
            <div className="px-4 pb-4 pt-1 space-y-3" data-testid="po-pay-form-panel">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className={lbl}>Nominal *</Label>
                  <NumberInput data-testid="po-payment-amount" value={pay.amount} onChange={(v) => setPay({ ...pay, amount: v })} className="bg-white rounded-xl h-11 mt-1" />
                </div>
                <div>
                  <Label className={lbl}>Tanggal Bayar</Label>
                  <DateInput data-testid="po-payment-date" value={pay.date} onChange={(e) => setPay({ ...pay, date: e.target.value })} className="bg-white rounded-xl h-11" wrapperClassName="mt-1" />
                </div>
                <div>
                  <Label className={lbl}>Metode</Label>
                  <Input data-testid="po-payment-method" value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })} placeholder="Transfer BCA, Tunai…" className="bg-white rounded-xl h-11 mt-1" />
                </div>
                <div>
                  <Label className={lbl}>Keterangan *</Label>
                  <Input data-testid="po-payment-note" value={pay.note} onChange={(e) => setPay({ ...pay, note: e.target.value })} placeholder="mis. DP 30%" className="bg-white rounded-xl h-11 mt-1" />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx" onChange={(e) => pickFile(e.target.files?.[0])} className="hidden" data-testid="po-payment-file" />
                <button type="button" onClick={() => fileRef.current?.click()} data-testid="po-payment-file-btn" className="h-9 px-4 rounded-full border border-[#B0C7EA] text-[#3B4A63] hover:bg-[#E5EEFA] text-xs font-semibold inline-flex items-center gap-1.5">
                  <Paperclip className="w-3.5 h-3.5" /> {file ? "Ganti Bukti" : "Lampirkan Bukti Transfer"}
                </button>
                {file && (
                  <div className="inline-flex items-center gap-1.5 text-[11px] text-[#3B4A63] bg-white rounded-full px-3 h-8 border border-[#B0C7EA]">
                    <FileText className="w-3 h-3" /> {file.name}
                    <button type="button" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-[#CE6968] ml-1"><X className="w-3 h-3" /></button>
                  </div>
                )}
                <div className="flex-1" />
                <button onClick={add} disabled={busy} data-testid="po-payment-save" className="h-9 px-5 rounded-full bg-gradient-to-r from-[#6B8FE2] to-[#3B4A63] text-white text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-60">
                  <Plus className="w-3.5 h-3.5" /> {busy ? "Menyimpan…" : "Simpan"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {canManage && locked && (
        <div className="rounded-2xl bg-[#FFF7E8] border border-[#F2D9A0] p-4 text-[12px] text-[#8C6318]" data-testid="po-pay-locked">
          {po.status === "void" ? "PO sudah dibatalkan — pembayaran tidak dapat dicatat." : "Terbitkan PO terlebih dahulu sebelum mencatat pembayaran."}
        </div>
      )}

      <div className="space-y-2">
        {(data.payments || []).length === 0 ? (
          <div className="rounded-2xl bg-white/50 border border-[#DCE0E7]/60 p-8 text-center text-sm text-[#A0A3A8]" data-testid="po-payments-empty">
            Belum ada pembayaran ke vendor.
          </div>
        ) : (
          data.payments.map((p) => (
            <div key={p.id} data-testid={`po-payment-${p.id}`} className="rounded-2xl bg-white border border-[#DCE0E7]/60 p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#57D28A] to-[#3EB876] text-white flex items-center justify-center shrink-0">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#090808] truncate">{p.note || "Pembayaran"}{p.method ? ` · ${p.method}` : ""}</div>
                <div className="text-[11px] text-[#A0A3A8] mt-0.5 flex items-center gap-2 flex-wrap">
                  <span className="font-num tabular-nums">{p.date ? fmtDate(p.date) : "-"}</span>
                  {p.attachment && (
                    <button onClick={() => openAttachment(p)} data-testid={`po-payment-attachment-${p.id}`} className="inline-flex items-center gap-1 text-[#6B8FE2] hover:underline">
                      <Paperclip className="w-3 h-3" /> bukti
                    </button>
                  )}
                </div>
              </div>
              <div className="text-base font-bold text-[#090808] font-num tabular-nums">{fmtIDR(p.amount)}</div>
              {canManage && (
                <button onClick={() => del(p.id)} data-testid={`po-payment-delete-${p.id}`} className="w-8 h-8 rounded-full hover:bg-[#FDE7E9] text-[#CE6968] flex items-center justify-center shrink-0">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
